import { useEffect, useMemo, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { Truck } from "lucide-react";
import { loadRemaining, paths, type Job, type StockLoad } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { CameraScanButton, itemCode } from "./ScanTools";

// Trucks. On a job: what's loaded for it, load more (search or scan a
// label), and send a door back to the yard. On an inventory item: load it
// for a job, or bring it back from one. Counts move through loadForJob /
// returnFromJob; "found & done" uses what's on the truck first.

type LoadRow = StockLoad & { id: string };
interface FieldItem {
  id: string;
  name: string;
  sku?: string;
  category: string;
  kind: string;
}
const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function useLoads(tenantId: string, field: "jobId" | "itemId", value: string): LoadRow[] {
  const [rows, setRows] = useState<LoadRow[]>([]);
  useEffect(
    () =>
      onSnapshot(
        query(collection(db, paths.tenantStockLoads(tenantId)), where(field, "==", value)),
        (s) => setRows(s.docs.map((d) => ({ id: d.id, ...(d.data() as StockLoad) })).sort((a, b) => b.loadedAt - a.loadedAt)),
        () => setRows([]),
      ),
    [tenantId, field, value],
  );
  return rows;
}

function useFieldCatalog(tenantId: string, enabled: boolean): FieldItem[] | null {
  const [catalog, setCatalog] = useState<FieldItem[] | null>(null);
  useEffect(() => {
    if (!enabled || catalog !== null) return;
    void call<{ items: FieldItem[] }>("listFieldPricebook", { tenantId }, { mutation: false })
      .then((r) => setCatalog(r.items.filter((i) => i.kind !== "kit")))
      .catch(() => setCatalog([]));
  }, [tenantId, enabled, catalog]);
  return catalog;
}

function ReturnButton({ tenantId, load, onDone }: { tenantId: string; load: LoadRow; onDone: (note: string) => void }) {
  const left = loadRemaining(load);
  const [qty, setQty] = useState(String(left));
  const [busy, setBusy] = useState(false);
  if (left <= 0) return null;
  return (
    <span className="flex items-center gap-1">
      {left > 1 ? <Input type="number" min={1} max={left} className="w-14 py-0.5 text-xs" value={qty} onChange={(e) => setQty(e.target.value)} aria-label="How many back" /> : null}
      <Button
        variant="ghost"
        className="px-2 py-0.5 text-xs"
        busy={busy}
        onClick={() => {
          setBusy(true);
          void call<{ note: string }>("returnFromJob", { tenantId, loadId: load.id, quantity: Math.max(1, Math.min(left, parseInt(qty, 10) || left)) })
            .then((r) => onDone(r.note))
            .catch((err: Error) => onDone(err.message))
            .finally(() => setBusy(false));
        }}
      >
        Back to yard
      </Button>
    </span>
  );
}

/** The job page: what's on the truck for this job. */
export function JobLoads({ tenantId, jobId, job }: { tenantId: string; jobId: string; job: Job }) {
  const loads = useLoads(tenantId, "jobId", jobId);
  const [adding, setAdding] = useState(false);
  const catalog = useFieldCatalog(tenantId, adding);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<FieldItem | null>(null);
  const [qty, setQty] = useState("1");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !catalog) return [];
    return catalog.filter((i) => `${i.name} ${i.category} ${i.sku ?? ""}`.toLowerCase().includes(q)).slice(0, 8);
  }, [catalog, search]);
  const open = loads.filter((l) => loadRemaining(l) > 0);
  const closed = loads.filter((l) => loadRemaining(l) === 0);
  if (!loads.length && !adding && (job.status === "done" || job.status === "canceled")) return null;

  async function load() {
    if (!picked) return;
    setBusy(true);
    setNote("");
    try {
      const r = await call<{ note: string }>("loadForJob", { tenantId, jobId, itemId: picked.id, quantity: Math.max(1, parseInt(qty, 10) || 1) });
      setNote(r.note);
      setPicked(null);
      setSearch("");
      setQty("1");
      setAdding(false);
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const scanned = (code: string) => {
    const hit = catalog?.find((i) => itemCode(i) === code || (i.sku && i.sku === code));
    if (hit) setPicked(hit);
    else setNote("No item has that label.");
  };

  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <CardTitle className="mb-0 flex items-center gap-1.5">
          <Truck size={15} className="text-steel" /> On the truck{open.length ? ` (${open.reduce((s, l) => s + loadRemaining(l), 0)})` : ""}
        </CardTitle>
        {job.status !== "done" ? (
          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setAdding((v) => !v)}>
            {adding ? "Cancel" : "+ Load"}
          </Button>
        ) : null}
      </div>
      {adding ? (
        <div className="mt-2 space-y-2 rounded-lg bg-cream p-2">
          {picked ? (
            <div className="flex flex-wrap items-end gap-2">
              <span className="min-w-0 flex-1 truncate text-sm text-ink">{picked.name}</span>
              <div className="w-20">
                <Label>How many</Label>
                <Input type="number" min={1} className="py-1 text-sm" value={qty} onChange={(e) => setQty(e.target.value)} />
              </div>
              <Button className="px-3 py-1 text-xs" busy={busy} onClick={() => void load()}>
                Load it
              </Button>
              <button className="text-xs text-steel underline hover:text-ink" onClick={() => setPicked(null)}>
                Other item
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input className="flex-1 py-1 text-sm" value={search} placeholder={catalog === null ? "Loading the pricebook…" : "Search (e.g. 16x7), or scan the label"} onChange={(e) => setSearch(e.target.value)} />
              <CameraScanButton onCode={scanned} />
            </div>
          )}
          {!picked && matches.length ? (
            <div className="rounded-lg bg-white">
              {matches.map((i) => (
                <button key={i.id} className="flex w-full items-center justify-between gap-2 border-b border-steel/10 px-2 py-1 text-left text-sm last:border-0 hover:bg-cream/60" onClick={() => setPicked(i)}>
                  <span className="truncate text-ink">{i.name}</span>
                  <span className="shrink-0 text-xs text-steel">{[i.category, i.sku].filter(Boolean).join(" · ")}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {note ? <p className="mt-1 text-xs text-ink">{note}</p> : null}
      <div className="mt-2">
        {open.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-steel/10 py-1 text-sm last:border-0">
            <span className="min-w-0 flex-1">
              <span className="text-ink">
                {loadRemaining(l)} × {l.itemName}
              </span>
              <span className="block text-xs text-steel">
                loaded {day(l.loadedAt)} by {l.loadedByName}
                {l.usedQty ? ` · ${l.usedQty} used` : ""}
                {l.returnedQty ? ` · ${l.returnedQty} back` : ""}
                {l.note ? ` · ${l.note}` : ""}
              </span>
            </span>
            <ReturnButton tenantId={tenantId} load={l} onDone={setNote} />
          </div>
        ))}
        {closed.map((l) => (
          <div key={l.id} className="py-1 text-xs text-steel">
            {l.quantity} × {l.itemName}: {l.status === "returned" ? "back in the yard" : `${l.usedQty} used${l.returnedQty ? `, ${l.returnedQty} back` : ""}`}
          </div>
        ))}
        {!loads.length && !adding ? <p className="py-1 text-xs text-steel">Nothing loaded for this job. The yard loads doors here or from Inventory, by scanning the label.</p> : null}
      </div>
    </Card>
  );
}

/** The inventory item panel: load this item for a job, or bring it back. */
export function TruckMoves({ tenantId, item, onFlash }: { tenantId: string; item: { id: string; name: string; kind?: string }; onFlash: (m: string) => void }) {
  const loads = useLoads(tenantId, "itemId", item.id).filter((l) => loadRemaining(l) > 0);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<Array<Job & { id: string }>>([]);
  const [jobSearch, setJobSearch] = useState("");
  const [jobId, setJobId] = useState("");
  const [qty, setQty] = useState("1");
  const [busy, setBusy] = useState(false);
  // Open jobs from yesterday on (and ones with no date yet), newest date first.
  useEffect(() => {
    if (!loading) return;
    const from = Date.now() - 86_400_000;
    return onSnapshot(
      query(collection(db, paths.tenantJobs(tenantId)), where("status", "in", ["scheduled", "in_progress"]), where("startAt", ">=", from), orderBy("startAt"), limit(300)),
      (s) => setJobs(s.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
      () => setJobs([]),
    );
  }, [tenantId, loading]);
  const shown = useMemo(() => {
    const q = jobSearch.trim().toLowerCase();
    return (q ? jobs.filter((j) => `${j.title} ${j.customerName ?? ""} ${j.lot ?? ""} ${j.address} ${j.poNumber ?? ""}`.toLowerCase().includes(q)) : jobs).slice(0, 12);
  }, [jobs, jobSearch]);
  if (item.kind === "kit") return null;

  async function load() {
    if (!jobId) return;
    setBusy(true);
    try {
      const r = await call<{ note: string }>("loadForJob", { tenantId, jobId, itemId: item.id, quantity: Math.max(1, parseInt(qty, 10) || 1) });
      onFlash(r.note);
      setLoading(false);
      setJobId("");
      setJobSearch("");
      setQty("1");
    } catch (err) {
      onFlash((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 rounded-lg border border-steel/15 bg-cream/40 p-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Truck size={14} className="text-steel" />
        <span className="font-semibold text-ink">Trucks</span>
        <span className="text-steel">{loads.length ? `${loads.reduce((s, l) => s + loadRemaining(l), 0)} out on ${new Set(loads.map((l) => l.jobId)).size} job${new Set(loads.map((l) => l.jobId)).size === 1 ? "" : "s"}` : "none out"}</span>
        <Button variant="ghost" className="ml-auto px-2 py-0.5 text-xs" onClick={() => setLoading((v) => !v)}>
          {loading ? "Cancel" : "Load for a job"}
        </Button>
      </div>
      {loading ? (
        <div className="mt-2 space-y-1.5">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-48 flex-1">
              <Label>Which job</Label>
              <Input className="py-1 text-sm" value={jobSearch} placeholder="Customer, lot, PO, address…" onChange={(e) => { setJobSearch(e.target.value); setJobId(""); }} />
            </div>
            <div className="w-20">
              <Label>How many</Label>
              <Input type="number" min={1} className="py-1 text-sm" value={qty} onChange={(e) => setQty(e.target.value)} />
            </div>
            <Button className="px-3 py-1 text-xs" busy={busy} disabled={!jobId} onClick={() => void load()}>
              Load
            </Button>
          </div>
          {!jobId ? (
            <div className="max-h-48 overflow-y-auto rounded-lg bg-white">
              {shown.map((j) => (
                <button key={j.id} className="flex w-full items-center justify-between gap-2 border-b border-steel/10 px-2 py-1 text-left text-xs last:border-0 hover:bg-cream/60" onClick={() => { setJobId(j.id); setJobSearch(`${j.customerName ? `${j.customerName} · ` : ""}${j.title}`); }}>
                  <span className="min-w-0 truncate text-ink">
                    {j.customerName ? `${j.customerName} · ` : ""}
                    {j.title}
                  </span>
                  <span className="shrink-0 text-steel">{j.startAt ? day(j.startAt) : "no date"}{j.lot ? ` · ${j.lot}` : ""}</span>
                </button>
              ))}
              {shown.length === 0 ? <p className="px-2 py-2 text-xs text-steel">{jobs.length ? "Nothing matches." : "No open jobs from yesterday on."}</p> : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {loads.length ? (
        <div className="mt-1.5">
          {loads.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 py-0.5 text-xs">
              <span className={cn("min-w-0 flex-1 truncate text-ink")}>
                {loadRemaining(l)} on the truck for <strong>{l.jobTitle}</strong> · {day(l.loadedAt)}
              </span>
              <ReturnButton tenantId={tenantId} load={l} onDone={onFlash} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
